// Authenticated proof-media Blob helper.
//
// Fetches proof image bytes through the `proof-media` edge function using the
// caller's Supabase session JWT (Authorization header), converts to a Blob
// URL, and caches with reference counting so multiple mounted components can
// share one fetch.
//
// Cache scoping (critical for tenant/user isolation):
//   key = `${projectRef}::${authUserId}::${tenantId}::${proofId}::${fileIndex}::${size}::${version}`
// where `version` is the proof's `created_at` epoch. On sign-out, auth-user
// change, or tenant switch, `invalidateAllProofMedia()` revokes every
// outstanding URL — a subsequent request under the new identity re-authorizes
// server-side.

import { supabase } from '@/integrations/supabase/client';

const PROJECT_ID = import.meta.env.VITE_SUPABASE_PROJECT_ID as string | undefined;
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

const FUNCTION_URL = `${SUPABASE_URL}/functions/v1/proof-media`;

// How long a fully-released Blob URL stays alive before we revoke it.
const IDLE_REVOKE_MS = 30_000;

export type ProofMediaSize = 'thumb' | 'full';

export interface ProofMediaRequest {
  proofId: string;
  fileIndex: number;
  size: ProofMediaSize;
  version: string | null; // proof.created_at (ISO)
}

export type ProofMediaErrorKind =
  | 'auth'
  | 'forbidden'
  | 'not_found'
  | 'transient'
  | 'aborted'
  | 'unknown';

export interface ProofMediaError {
  kind: ProofMediaErrorKind;
  status: number | null;
  message: string;
  terminal: boolean;
}

interface CacheEntry {
  url: string;
  refcount: number;
  idleTimer: ReturnType<typeof setTimeout> | null;
}

interface ScopeIdentity {
  projectRef: string;
  authUserId: string;
  tenantId: string;
}

let currentScope: ScopeIdentity | null = null;

const blobUrlCache = new Map<string, CacheEntry>();
const inFlightCache = new Map<string, Promise<string>>();

function projectRef(): string {
  if (PROJECT_ID) return PROJECT_ID;
  try {
    return new URL(SUPABASE_URL).host.split('.')[0];
  } catch {
    return 'unknown-project';
  }
}

export function setProofMediaScope(next: ScopeIdentity | null): void {
  const same =
    currentScope &&
    next &&
    currentScope.projectRef === next.projectRef &&
    currentScope.authUserId === next.authUserId &&
    currentScope.tenantId === next.tenantId;
  if (same) {
    currentScope = next;
    return;
  }
  invalidateAllProofMedia('scope-change');
  currentScope = next;
}

export function invalidateAllProofMedia(
  _reason: 'sign-out' | 'scope-change' | 'manual' = 'manual',
): void {
  for (const entry of blobUrlCache.values()) {
    if (entry.idleTimer) clearTimeout(entry.idleTimer);
    try {
      URL.revokeObjectURL(entry.url);
    } catch {
      // ignore
    }
  }
  blobUrlCache.clear();
  inFlightCache.clear();
}

function buildKey(req: ProofMediaRequest, scope: ScopeIdentity): string {
  return [
    scope.projectRef,
    scope.authUserId,
    scope.tenantId,
    req.proofId,
    req.fileIndex,
    req.size,
    req.version ?? '0',
  ].join('::');
}

function shortHash(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(16).padStart(8, '0');
}

interface FetchTiming {
  fetchStartAt: number;
  fetchEndAt: number;
}

async function fetchWithAuth(
  req: ProofMediaRequest,
  signal: AbortSignal | undefined,
  timing: FetchTiming,
): Promise<Blob> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) {
    const err: ProofMediaError = {
      kind: 'auth',
      status: 401,
      message: 'No active session',
      terminal: true,
    };
    throw err;
  }

  const url =
    `${FUNCTION_URL}?proofId=${encodeURIComponent(req.proofId)}` +
    `&fileIndex=${encodeURIComponent(String(req.fileIndex))}` +
    `&size=${encodeURIComponent(req.size)}`;

  const doFetch = async (bearer: string) =>
    fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${bearer}`,
        apikey: ANON_KEY,
      },
      signal,
    });

  timing.fetchStartAt = performance.now();

  let res = await doFetch(token);

  // One-shot 401 recovery: refresh the session and replay once.
  if (res.status === 401) {
    const { data: refreshed } = await supabase.auth.refreshSession();
    const nextToken = refreshed?.session?.access_token;
    if (nextToken) {
      res = await doFetch(nextToken);
    }
  }

  if (res.status === 401) {
    throw { kind: 'auth', status: 401, message: 'Unauthorized', terminal: true } as ProofMediaError;
  }
  if (res.status === 403) {
    throw { kind: 'forbidden', status: 403, message: 'Forbidden', terminal: true } as ProofMediaError;
  }
  if (res.status === 404) {
    throw { kind: 'not_found', status: 404, message: 'Not found', terminal: true } as ProofMediaError;
  }
  if (!res.ok) {
    throw {
      kind: 'transient',
      status: res.status,
      message: `HTTP ${res.status}`,
      terminal: false,
    } as ProofMediaError;
  }

  const blob = await res.blob();
  timing.fetchEndAt = performance.now();
  if (!blob || blob.size === 0) {
    throw {
      kind: 'transient',
      status: res.status,
      message: 'Empty body',
      terminal: false,
    } as ProofMediaError;
  }
  return blob;
}

export type ProofMediaNetworkSample =
  | { performed: false }
  | {
      performed: true;
      ok: boolean;
      status: number | null;
      durationMs: number;
      joinedInFlight: boolean;
    };

export interface ProofMediaAcquireResult {
  url: string;
  release: () => void;
  network: ProofMediaNetworkSample;
}

/**
 * Acquire a Blob URL for the given proof file. Increments a refcount — the
 * caller MUST call `release()` when unmounting so the cache entry can be
 * revoked once idle.
 */
export async function acquireProofMediaBlobUrl(
  req: ProofMediaRequest,
  opts: { signal?: AbortSignal } = {},
): Promise<ProofMediaAcquireResult> {
  const scope = currentScope;
  if (!scope) {
    throw {
      kind: 'auth',
      status: 401,
      message: 'No active scope (sign in)',
      terminal: true,
    } as ProofMediaError;
  }

  const key = buildKey(req, scope);
  const existing = blobUrlCache.get(key);
  if (existing) {
    existing.refcount += 1;
    if (existing.idleTimer) {
      clearTimeout(existing.idleTimer);
      existing.idleTimer = null;
    }
    return {
      url: existing.url,
      release: () => release(key),
      network: { performed: false },
    };
  }

  let promise = inFlightCache.get(key);
  let joinedInFlight = false;

  let enqueueAt = 0;
  const timing: FetchTiming = { fetchStartAt: 0, fetchEndAt: 0 };

  if (!promise) {
    enqueueAt = performance.now();
    promise = (async () => {
      try {
        const blob = await fetchWithAuth(req, opts.signal, timing);
        const objectUrl = URL.createObjectURL(blob);
        const blobReadyAt = performance.now();
        blobUrlCache.set(key, { url: objectUrl, refcount: 0, idleTimer: null });

        // Permanent slow-fetch diagnostic (PRODUCTION). Payload contains only
        // timings + concurrency — no proofIds, no file paths, no PII.
        const queueWaitMs = Math.round(timing.fetchStartAt - enqueueAt);
        const fetchDurationMs = Math.round(timing.fetchEndAt - timing.fetchStartAt);
        const blobCreationMs = Math.round(blobReadyAt - timing.fetchEndAt);
        const totalDurationMs = Math.round(blobReadyAt - enqueueAt);
        if (fetchDurationMs > 1000) {
          // eslint-disable-next-line no-console
          console.warn('[proof-proxy][slow-fetch]', {
            queueWaitMs,
            fetchDurationMs,
            blobCreationMs,
            totalDurationMs,
            concurrency: DEFAULT_PROOF_PROXY_CONCURRENCY,
          });
        }
        return objectUrl;
      } finally {
        inFlightCache.delete(key);
      }
    })();
    inFlightCache.set(key, promise);
  } else {
    joinedInFlight = true;
  }

  const awaitStartedAt = performance.now();
  let objectUrl: string;
  try {
    objectUrl = await promise;
  } catch (err) {
    const pe = err as ProofMediaError;
    const durationMs = Math.round(performance.now() - awaitStartedAt);
    throw {
      ...pe,
      network: {
        performed: true,
        ok: false,
        status: pe?.status ?? null,
        durationMs,
        joinedInFlight,
      },
    } as ProofMediaError & { network: ProofMediaNetworkSample };
  }
  const durationMs = Math.round(performance.now() - awaitStartedAt);

  if (currentScope !== scope) {
    const entry = blobUrlCache.get(key);
    if (entry) {
      try { URL.revokeObjectURL(entry.url); } catch { /* noop */ }
      blobUrlCache.delete(key);
    }
    throw {
      kind: 'aborted',
      status: null,
      message: 'Scope changed during fetch',
      terminal: true,
    } as ProofMediaError;
  }

  const entry = blobUrlCache.get(key);
  if (!entry) {
    throw {
      kind: 'aborted',
      status: null,
      message: 'Cache invalidated during fetch',
      terminal: true,
    } as ProofMediaError;
  }
  entry.refcount += 1;
  if (entry.idleTimer) {
    clearTimeout(entry.idleTimer);
    entry.idleTimer = null;
  }
  return {
    url: objectUrl,
    release: () => release(key),
    network: {
      performed: true,
      ok: true,
      status: 200,
      durationMs,
      joinedInFlight,
    },
  };
}

function release(key: string): void {
  const entry = blobUrlCache.get(key);
  if (!entry) return;
  entry.refcount = Math.max(0, entry.refcount - 1);
  if (entry.refcount === 0 && !entry.idleTimer) {
    entry.idleTimer = setTimeout(() => {
      const still = blobUrlCache.get(key);
      if (!still || still.refcount > 0) return;
      try { URL.revokeObjectURL(still.url); } catch { /* noop */ }
      blobUrlCache.delete(key);
    }, IDLE_REVOKE_MS);
  }
}

/**
 * Full-size download helper. Acquires a `size: 'full'` Blob URL through the
 * shared cache (auth + dedup + refcount reused), triggers a browser download,
 * and releases the reference in `finally`. Never calls `createSignedUrl` —
 * the proxy path is exclusive.
 */
export async function downloadProofMediaFull(
  req: Omit<ProofMediaRequest, 'size'>,
  fileName: string,
): Promise<void> {
  const { url, release } = await acquireProofMediaBlobUrl({ ...req, size: 'full' });
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    try { release(); } catch { /* noop */ }
  }
}

// Production default. Six concurrent thumbnail requests reduce the wait for
// multi-photo pending orders while retaining the shared cache and deduplication.
export type ProofProxyConcurrency = 1 | 3 | 4 | 6;
export const DEFAULT_PROOF_PROXY_CONCURRENCY: ProofProxyConcurrency = 6;

/**
 * Kept as an exported accessor for API stability across the codebase. Since
 * Stage 2 GA, the concurrency is fixed to the production default — the
 * localStorage override was retired alongside the benchmark harness.
 */
export function readProofProxyConcurrency(): {
  key: string;
  storedValue: string | null;
  resolvedValue: ProofProxyConcurrency;
} {
  return {
    key: `proof-proxy-concurrency:${projectRef()}`,
    storedValue: null,
    resolvedValue: DEFAULT_PROOF_PROXY_CONCURRENCY,
  };
}

/**
 * Dev/preview gate for the two remaining diagnostic logs (render-source,
 * thumbnail-load). Never fires on the real production domain unless the
 * operator opts in via `localStorage['proof-debug:enabled'] = '1'`.
 */
export function isProofDebugAllowed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (import.meta.env.DEV) return true;
    if (window.localStorage.getItem('proof-debug:enabled') === '1') return true;
    const host = window.location.hostname;
    if (
      host === 'localhost' ||
      host.endsWith('.lovable.app') ||
      host.endsWith('.lovable.dev') ||
      host.endsWith('.lovableproject.com')
    ) {
      return true;
    }
  } catch {
    /* noop */
  }
  return false;
}

export function getProofMediaProjectRef(): string {
  return projectRef();
}
